// frontend/src/pages/VehicleDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { vehiclesApi } from '../api/vehicles';
import { inspectionsApi } from '../api/inspections';
import { settingsApi } from '../api/settings'; 
import helpers from '../utils/helpers';
import { 
    FaShoppingCart, FaArrowLeft, FaCheck, FaPhone, FaWhatsapp, FaClipboardCheck, FaCheckCircle, 
    FaExclamationTriangle, FaTimesCircle, FaShieldAlt, FaLock, FaMoneyBillWave, FaUndo, FaEye,
    FaFlag, FaStore, FaHeadset, FaUserSlash, FaMapMarkerAlt, FaTachometerAlt, FaCog, FaPalette, FaRocket, FaEdit, FaTag
} from 'react-icons/fa';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext'; 
import SaveButton from '../components/common/SaveButton';
import ReviewSection from '../components/reviews/ReviewSection';
import DynamicMeta from '../components/common/DynamicMeta';
import ReportModal from '../components/common/ReportModal';
import BoostModal from '../components/common/BoostModal'; 
import VehicleCard from '../components/vehicle/VehicleCard';

// --- CONFIGURATION CONSTANT ---
const EXCLUSIVE_STORE_ID = "everything_automotive_exclusive";
// ------------------------------

function VehicleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate(); 
  const { addToCart } = useCart();
  const { user, isAuthenticated } = useAuth(); 
  
  const [vehicle, setVehicle] = useState(null);
  const [report, setReport] = useState(null);
  const [relatedVehicles, setRelatedVehicles] = useState([]);
  const [sellerVehicles, setSellerVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImage, setActiveImage] = useState(null);
  
  // Contact State
  const [showContact, setShowContact] = useState(true);
  const [isPhoneRevealed, setIsPhoneRevealed] = useState(false);

  // Modals
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isBoostModalOpen, setIsBoostModalOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [vehicleData, reportData, settingsData] = await Promise.all([
            vehiclesApi.getVehicleById(id),
            inspectionsApi.getVehicleReport(id).catch(() => null),
            settingsApi.getAllSettings()
        ]);

        setVehicle(vehicleData);
        setReport(reportData);
        
        const siteContent = settingsData.site_content || {};
        if (siteContent.show_contact_vehicles !== undefined) {
            setShowContact(siteContent.show_contact_vehicles);
        }

        const initialImage = vehicleData.images?.front || Object.values(vehicleData.images || {})[0];
        setActiveImage(initialImage);

        try {
            const [related, sellerItems] = await Promise.all([
                vehiclesApi.getRelatedVehicles(id),
                vehiclesApi.getSellerVehicles(id)
            ]);
            setRelatedVehicles(related);
            setSellerVehicles(sellerItems);
        } catch (e) {
            console.error("Failed to fetch additional vehicles", e);
        }

      } catch (err) {
        console.error("Error fetching details:", err);
        setError("Failed to load vehicle details.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // --- PRICE CALCULATION HELPER ---
  const getPriceDetails = () => {
      if (!vehicle) return { original: 0, final: 0, hasDiscount: false };
      
      const original = parseFloat(vehicle.price) || 0;
      const discount = parseFloat(vehicle.discount_percentage) || 0;
      
      if (discount > 0) {
          const saved = original * (discount / 100);
          return { 
              original: original, 
              final: original - saved, 
              hasDiscount: true, 
              percent: discount 
          };
      }
      return { original: original, final: original, hasDiscount: false, percent: 0 };
  };

  const handleAddToCart = () => {
      if (vehicle) {
          const { final } = getPriceDetails();
          addToCart({
              id: vehicle.id,
              itemname: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
              price: final, // Add the discounted price to cart
              image: vehicle.images?.front || Object.values(vehicle.images || {})[0],
              type: 'vehicle'
          });
          alert("Vehicle added to cart!");
      }
  };

  const getStatusConfig = (status) => {
      switch(status) {
          case 'good': return { icon: FaCheckCircle, color: 'text-green-500', bg: 'bg-green-50', border: 'border-green-200', label: 'Good' };
          case 'fair': return { icon: FaExclamationTriangle, color: 'text-yellow-500', bg: 'bg-yellow-50', border: 'border-yellow-200', label: 'Fair' };
          case 'poor': return { icon: FaTimesCircle, color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-200', label: 'Poor' };
          default: return { icon: FaCheck, color: 'text-gray-400', bg: 'bg-gray-50', border: 'border-gray-200', label: 'N/A' };
      }
  };

  const getBadgeConfig = (status) => {
      switch(status) {
          case 'certified': return { color: 'bg-green-600', text: 'Certified', icon: FaShieldAlt };
          case 'inspected': return { color: 'bg-yellow-500', text: 'Inspected', icon: FaClipboardCheck };
          case 'failed': return { color: 'bg-red-600', text: 'Needs Repair', icon: FaExclamationTriangle };
          default: return null;
      }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
        <p className="text-gray-700 mb-6">{error || "Vehicle not found."}</p>
        <Link to="/vehicles/browse" className="text-primary hover:underline flex items-center justify-center gap-2">
          <FaArrowLeft /> Back to Marketplace
        </Link>
      </div>
    );
  }

  const badge = report ? getBadgeConfig(report.certification_status) : null;
  const { original, final, hasDiscount, percent } = getPriceDetails();
  
  const vehicleTitle = `${vehicle.year} ${vehicle.make} ${vehicle.model} - ${helpers.formatCurrency(final, 'NGN')}`;
  const vehicleDescription = `Condition: ${vehicle.condition.replace('_', ' ')}. Mileage: ${vehicle.mileage.toLocaleString()} km. Location: ${vehicle.location_lga}, ${vehicle.location_state}.`;
  const mainImage = vehicle.images?.front || Object.values(vehicle.images || {})[0] || 'https://via.placeholder.com/400x300?text=Everything+Automotive';

  // --- OWNER CHECK ---
  const isOwner = user && (user.id === vehicle.user_id || user.roles.includes('admin') || user.roles.includes('super_admin'));
  const isPromoted = vehicle.is_promoted && new Date(vehicle.promoted_until) > new Date();

  // --- REUSABLE SIDEBAR CONTENT ---
  const SidebarContent = () => (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
      
      {/* --- OWNER CONTROLS --- */}
      {isOwner && (
          <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2">
                  <FaUserSlash /> Owner Controls
              </h4>
              <div className="grid grid-cols-2 gap-2">
                  <button 
                      onClick={() => setIsBoostModalOpen(true)}
                      className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-2 px-3 rounded flex items-center justify-center gap-2 transition shadow-sm"
                  >
                      <FaRocket /> {isPromoted ? 'Extend Boost' : 'Boost Listing'}
                  </button>
                  <Link 
                      to={`/vehicles/edit/${vehicle.id}`}
                      className="bg-white border border-blue-300 text-blue-700 text-xs font-bold py-2 px-3 rounded flex items-center justify-center gap-2 hover:bg-blue-50 transition"
                  >
                      <FaEdit /> Edit Listing
                  </Link>
              </div>
              {isPromoted && (
                  <p className="text-xs text-red-600 font-bold mt-2 text-center">
                      🔥 Boost Active until {new Date(vehicle.promoted_until).toLocaleDateString()}
                  </p>
              )}
          </div>
      )}

      <div className="mb-2">
        <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded uppercase font-semibold tracking-wide">
          {vehicle.condition.replace('_', ' ')}
        </span>
      </div>

      {/* TITLE & SAVE BUTTON */}
      <div className="flex justify-between items-start mb-2 gap-2">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight break-words">
            {vehicle.year} {vehicle.make} {vehicle.model}
        </h1>
        <div className="mt-1 flex-shrink-0">
            <SaveButton itemId={vehicle.id} itemType="vehicle" className="text-xl" />
        </div>
      </div>

      {/* --- STORE NAME & LOCATION --- */}
      <div className="mb-4 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-2 text-gray-700 font-bold mb-1">
              <FaStore className="text-blue-500" />
              {vehicle.store_id ? (
                  <Link to={`/store/${vehicle.store_id}`} className="hover:text-primary hover:underline">
                      {vehicle.store_name || vehicle.seller_name || 'Private Seller'}
                  </Link>
              ) : (
                  <span>{vehicle.store_name || vehicle.seller_name || 'Private Seller'}</span>
              )}
          </div>
          <div className="flex items-center text-gray-500 text-sm ml-6">
              <FaMapMarkerAlt className="mr-1 text-gray-400" />
              {vehicle.location_lga}, {vehicle.location_state}
          </div>
      </div>

      {/* --- PRICE DISPLAY (UPDATED) --- */}
      <div className="mb-6">
          {hasDiscount ? (
              <div>
                  <div className="flex items-center gap-2 mb-1">
                      <span className="text-gray-400 line-through text-lg font-medium">
                          {helpers.formatCurrency(original, 'NGN')}
                      </span>
                      <span className="bg-red-100 text-red-600 text-xs font-bold px-2 py-1 rounded flex items-center gap-1">
                          <FaTag /> -{percent}% OFF
                      </span>
                  </div>
                  <div className="text-3xl md:text-4xl font-bold text-red-600">
                      {helpers.formatCurrency(final, 'NGN')}
                  </div>
              </div>
          ) : (
              <div className="text-3xl md:text-4xl font-bold text-primary">
                  {helpers.formatCurrency(final, 'NGN')}
              </div>
          )}
      </div>

      {/* --- ACTION BUTTONS --- */}
      <div className="space-y-3 mb-8">
          <button 
              onClick={handleAddToCart}
              className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-4 px-6 rounded-lg shadow-lg transition duration-300 flex items-center justify-center gap-2 text-lg"
          >
              <FaShoppingCart /> Buy Now
          </button>

          {showContact && (
              vehicle.seller_phone ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {isPhoneRevealed ? (
                          <a href={`tel:${vehicle.seller_phone}`} className="bg-white border-2 border-green-600 text-green-600 font-bold py-3 rounded-lg flex items-center justify-center gap-2 hover:bg-green-50 transition">
                              <FaPhone /> {vehicle.seller_phone}
                          </a>
                      ) : (
                          <button 
                              onClick={() => setIsPhoneRevealed(true)}
                              className="bg-white border-2 border-green-600 text-green-600 font-bold py-3 rounded-lg flex items-center justify-center gap-2 hover:bg-green-50 transition"
                          >
                              <FaEye /> Show Phone
                          </button>
                      )}
                      
                      <a href={`https://wa.me/${vehicle.seller_phone.replace('+', '')}`} target="_blank" rel="noopener noreferrer" className="bg-green-600 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 hover:bg-green-700 transition">
                          <FaWhatsapp className="text-xl" /> WhatsApp
                      </a>
                  </div>
              ) : (
                  <Link to="/contact" className="block w-full bg-gray-100 text-gray-700 font-bold py-3 rounded-lg text-center hover:bg-gray-200 transition flex items-center justify-center gap-2">
                      <FaHeadset /> Contact Support
                  </Link>
              )
          )}
      </div>

      {/* --- SAFETY WARNING --- */}
      {vehicle.pay_on_delivery_status === 'Available' && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r-lg">
              <div className="flex items-start gap-3">
                  <FaExclamationTriangle className="text-red-500 text-xl mt-1 flex-shrink-0" />
                  <div>
                      <h4 className="font-bold text-red-800 text-sm">Safety Warning</h4>
                      <p className="text-sm text-red-700">
                          Do not pay this seller until you have received the item and verified it is exactly what you ordered.
                      </p>
                  </div>
              </div>
          </div>
      )}

      {/* Key Specs Grid */}
      <div className="grid grid-cols-2 gap-4 mb-8 border-t border-b border-gray-100 py-6">
        <div>
          <span className="block text-xs text-gray-500 uppercase">Mileage</span>
          <div className="flex items-center font-semibold text-gray-800 truncate">
            <FaTachometerAlt className="mr-2 text-gray-400 flex-shrink-0" />
            {vehicle.mileage.toLocaleString()} km
          </div>
        </div>
        <div>
          <span className="block text-xs text-gray-500 uppercase">Transmission</span>
          <div className="flex items-center font-semibold text-gray-800 truncate">
            <FaCog className="mr-2 text-gray-400 flex-shrink-0" />
            <span className="capitalize">{vehicle.transmission}</span>
          </div>
        </div>
        <div className="mt-2">
          <span className="block text-xs text-gray-500 uppercase">Color</span>
          <div className="flex items-center font-semibold text-gray-800 truncate">
            <FaPalette className="mr-2 text-gray-400 flex-shrink-0" />
            {vehicle.color}
          </div>
        </div>
        <div className="mt-2">
          <span className="block text-xs text-gray-500 uppercase">Stock ID</span>
          <div className="font-mono text-xs text-gray-500 mt-1 truncate">
            {vehicle.id.substring(0, 8).toUpperCase()}
          </div>
        </div>
      </div>

      {/* --- POLICY TAGS --- */}
      <div className="mt-4">
          <h3 className="text-sm font-bold text-gray-500 uppercase mb-3">Policies</h3>
          <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-3">
            {vehicle.pay_on_delivery_status === 'Available' ? (
              <div className="flex items-center gap-3 text-green-700">
                <FaMoneyBillWave className="flex-shrink-0" />
                <strong>Pay on Delivery Available</strong>
              </div>
            ) : (
              <div className="flex items-center gap-3 text-red-700">
                <FaTimesCircle className="flex-shrink-0" />
                <span>Pay on Delivery Not Available</span>
              </div>
            )}

            {vehicle.refundable_status === 'Refundable' ? (
              <div className="flex items-center gap-3 text-green-700">
                <FaUndo className="flex-shrink-0" />
                <strong>Item is Refundable</strong>
              </div>
            ) : (
              <div className="flex items-center gap-3 text-red-700">
                <FaTimesCircle className="flex-shrink-0" />
                <span>Item is Non-Refundable</span>
              </div>
            )}
          </div>
      </div>

      <p className="text-xs text-gray-400 text-center mt-4">
        Reference ID: {vehicle.id}
      </p>

      {/* --- REPORT BUTTON --- */}
      <div className="mt-6 text-center border-t pt-4">
          <button 
              onClick={() => setIsReportModalOpen(true)}
              className="text-gray-400 hover:text-red-500 text-xs flex items-center justify-center gap-1 mx-auto transition-colors"
          >
              <FaFlag /> Report this listing
          </button>
      </div>
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen py-8">
      <DynamicMeta 
          title={vehicleTitle}
          description={vehicleDescription}
          image={mainImage}
          url={`${window.location.origin}/vehicles/${id}`}
      />
        
      <div className="container mx-auto px-4">
        
        {/* --- STATUS WARNING BANNER --- */}
        {vehicle.status !== 'active' && (
            <div className={`mb-6 p-4 rounded-lg border flex items-center gap-3 ${
                vehicle.status === 'rejected' 
                ? 'bg-red-100 border-red-300 text-red-800' 
                : 'bg-yellow-100 border-yellow-300 text-yellow-800'
            }`}>
                {vehicle.status === 'rejected' ? <FaTimesCircle className="text-xl" /> : <FaLock className="text-xl" />}
                <div>
                    <h3 className="font-bold text-lg">
                        {vehicle.status === 'rejected' ? 'Listing Rejected' : 'Pending Review'}
                    </h3>
                    <p className="text-sm">
                        This vehicle is <strong>not visible</strong> in the public marketplace search results. 
                        {vehicle.status === 'pending_review' && " It is currently visible only to the seller and admins for preview."}
                    </p>
                </div>
            </div>
        )}

        {/* Breadcrumb / Back */}
        <div className="mb-6">
          <Link to="/vehicles/browse" className="text-gray-500 hover:text-primary flex items-center gap-2 text-sm">
            <FaArrowLeft /> Back to Marketplace
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* LEFT COLUMN: Images & Report */}
          <div className="lg:col-span-2">
            
            {/* Main Image */}
            <div className="bg-black rounded-lg overflow-hidden h-64 md:h-96 flex items-center justify-center mb-4 shadow-md relative">
              <img 
                src={activeImage || 'https://via.placeholder.com/800x600?text=No+Image'} 
                alt={vehicle.model} 
                className="max-h-full max-w-full object-contain"
              />
              {badge && (
                  <div className={`absolute top-4 left-4 ${badge.color} text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 font-bold z-10`}>
                      <badge.icon /> {badge.text}
                  </div>
              )}
              {/* --- DISCOUNT BADGE ON IMAGE --- */}
              {hasDiscount && (
                  <div className="absolute top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-full shadow-lg font-black text-lg z-10 animate-pulse">
                      {percent}% OFF
                  </div>
              )}
            </div>

            {/* Thumbnails Gallery */}
            {vehicle.images && Object.keys(vehicle.images).length > 0 && (
              <div className="flex gap-3 overflow-x-auto pb-2 mb-8">
                {Object.entries(vehicle.images).map(([viewType, url]) => (
                  <button 
                    key={viewType}
                    onClick={() => setActiveImage(url)}
                    className={`relative w-20 h-20 md:w-24 md:h-24 flex-shrink-0 border-2 rounded-md overflow-hidden transition-all ${activeImage === url ? 'border-primary ring-2 ring-primary ring-opacity-50' : 'border-transparent opacity-70 hover:opacity-100'}`}
                  >
                    <img src={url} alt={viewType} className="w-full h-full object-cover" />
                    <span className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-[10px] text-center py-0.5 capitalize">
                      {viewType.replace('_', ' ')}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* --- MOBILE ONLY: SIDEBAR CONTENT --- */}
            <div className="block lg:hidden mb-8">
                <SidebarContent />
            </div>

            {/* --- INSPECTION REPORT SECTION --- */}
            {report ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
                    <div className="flex items-center justify-between border-b border-gray-100 pb-4 mb-6">
                        <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                            <FaClipboardCheck className="text-primary" /> Vehicle Health Report
                        </h3>
                        <div className="text-right">
                            <span className="block text-3xl font-bold text-gray-800">{report.overall_score}<span className="text-lg text-gray-400">/100</span></span>
                            <span className="text-xs text-gray-500 uppercase font-bold">Health Score</span>
                        </div>
                    </div>

                    {/* Verdict */}
                    <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500 mb-6">
                        <h4 className="font-bold text-blue-800 text-sm mb-1">Inspector's Verdict</h4>
                        <p className="text-blue-700 text-sm italic">"{report.inspector_verdict}"</p>
                        <p className="text-xs text-blue-500 mt-2 text-right">- Inspected by {report.inspector_name} on {new Date(report.created_at).toLocaleDateString()}</p>
                    </div>

                    {/* Components Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {['engine', 'transmission', 'suspension', 'brakes', 'electrical', 'body_frame', 'tires_wheels', 'interior'].map(key => {
                            const item = report[key];
                            if (!item) return null;
                            const config = getStatusConfig(item.status);
                            const Icon = config.icon;

                            return (
                                <div key={key} className={`p-3 rounded border ${config.bg} ${config.border} flex items-start gap-3`}>
                                    <Icon className={`mt-1 text-lg ${config.color}`} />
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-gray-800 capitalize">{key.replace('_', ' ')}</span>
                                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full bg-white border ${config.border} ${config.color}`}>
                                                {config.label}
                                            </span>
                                        </div>
                                        {item.notes && <p className="text-xs text-gray-600 mt-1">{item.notes}</p>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : (
                <div className="bg-gray-100 rounded-lg p-6 mb-8 text-center border border-dashed border-gray-300">
                    <FaClipboardCheck className="text-gray-400 text-4xl mx-auto mb-2" />
                    <h3 className="font-bold text-gray-600">No Inspection Report Available</h3>
                    <p className="text-sm text-gray-500">This vehicle has not been verified by our technicians yet.</p>
                </div>
            )}

            {/* Description & Features */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Description</h3>
              <p className="text-gray-700 whitespace-pre-line mb-8 break-words text-justify">
                {vehicle.description || "No description provided."}
              </p>

              <h3 className="text-xl font-bold text-gray-800 mb-4">Features</h3>
              {vehicle.features && vehicle.features.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {vehicle.features.map((feature, index) => (
                    <div key={index} className="flex items-center text-gray-600 text-sm">
                      <FaCheck className="text-green-500 mr-2 flex-shrink-0" /> 
                      <span className="truncate">{feature}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic">No specific features listed.</p>
              )}
            </div>
          </div>

          {/* RIGHT COLUMN: Details & Contact (DESKTOP ONLY) */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-24">
                <SidebarContent />
            </div>
          </div>

        </div>

        {/* --- RELATED VEHICLES SECTION --- */}
        {relatedVehicles.length > 0 && (
            <div className="mt-16 border-t border-gray-200 pt-10">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">You Might Also Like</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                    {relatedVehicles.map(related => (
                        <VehicleCard key={related.id} vehicle={related} />
                    ))}
                </div>
            </div>
        )}

        {/* --- MORE FROM SELLER SECTION --- */}
        {sellerVehicles.length > 0 && (
            <div className="mt-16 border-t border-gray-200 pt-10">
                <div className="flex items-center gap-2 mb-6">
                    <FaStore className="text-blue-600 text-2xl" />
                    <h3 className="text-2xl font-bold text-gray-800">
                        More from {vehicle.seller_name || 'This Seller'}
                    </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                    {sellerVehicles.map(sellerItem => (
                        <VehicleCard key={sellerItem.id} vehicle={sellerItem} />
                    ))}
                </div>
            </div>
        )}

      </div>

      {/* --- REPORT MODAL --- */}
      <ReportModal 
          isOpen={isReportModalOpen} 
          onClose={() => setIsReportModalOpen(false)} 
          itemId={id}
          itemName={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
          itemType="vehicle"
      />

      {/* --- BOOST MODAL --- */}
      {isBoostModalOpen && (
          <BoostModal 
              isOpen={isBoostModalOpen}
              onClose={() => setIsBoostModalOpen(false)}
              itemId={vehicle.id}
              itemType="vehicle"
              itemName={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
          />
      )}

    </div>
  );
}

export default VehicleDetailPage;